import {
  District,
  Location,
  LocationHierarchy,
  State,
} from '@/model/locations';
import { OfferWithImage } from '@/model/offer-with-image';
import { getHierarchyForOffer } from '@/service/data.service';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BehaviorSubject, Observable, map, of, shareReplay } from 'rxjs';
interface LocationSelectorValue {
  allAustria: boolean;
  selectedStates: number[];
  selectedDistricts: number[];
  selectedLocations: Location[];
}

@Component({
    selector: 'app-offer-location-selector',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatCheckboxModule,
        MatInputModule,
        MatAutocompleteModule,
        MatChipsModule,
        MatIconModule,
    ],
    templateUrl: './offer-location-selector.component.html',
    styleUrl: './offer-location-selector.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OfferLocationSelectorComponent),
            multi: true,
        },
    ]
})
export class OfferLocationSelectorComponent
  implements OnInit, ControlValueAccessor, AfterViewInit
{
  @Input() locations!: LocationHierarchy;
  @Input() offer: OfferWithImage | null = null;

  locationForm: FormGroup;
  states: State[] = [];
  districts: District[] = [];
  filteredLocations: Observable<Location[]> = of([]);
  allLocations: Location[] = [];
  private locationCache: Map<string, Location[]> = new Map<
    string,
    Location[]
  >();
  private selectedStatesSet: Set<number> = new Set<number>();
  private selectedDistrictsSet: Set<number> = new Set<number>();
  private locationSearchSubject = new BehaviorSubject<string>('');

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: LocationSelectorValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor(private fb: FormBuilder) {
    this.locationForm = this.fb.group({
      allAustria: [true],
      selectedStates: [[]],
      selectedDistricts: [[]],
      locationSearch: [''],
      selectedLocations: [[]],
    });
  }

  ngAfterViewInit() {
    const isAllAustria =
      this.offer?.bezirke?.length === 0 &&
      this.offer?.orte?.length === 0 &&
      this.offer?.bundeslaender?.length === 0;

    this.locationForm.get('allAustria')?.setValue(isAllAustria);
  }

  ngOnInit() {
    if (this.locations) {
      this.states = this.locations;
      this.allLocations = this.flattenLocations(this.locations);

      // Setup location search with debounce and caching
      this.filteredLocations = this.locationSearchSubject.pipe(
        map((value) => this._filterLocations(value || '')),
        shareReplay(1)
      );
      console.log(this.offer);
      console.log(this.locations);

      const isAllAustria =
        this.offer?.bezirke?.length === 0 &&
        this.offer?.orte?.length === 0 &&
        this.offer?.bundeslaender?.length === 0;

      // Initialize form with offer data if available
      if (this.offer && this.locations && !isAllAustria) {
        const hierarchy = getHierarchyForOffer(this.locations, this.offer);
        console.log('Hierarchy', hierarchy);
        // Update the locations with the reconstructed hierarchy
        this.locations = hierarchy;
        this.states = hierarchy;

        // Collect all selected items from the hierarchy
        const selectedStates: number[] = [];
        const selectedDistricts: number[] = [];
        const selectedLocations: Location[] = [];

        hierarchy.forEach((state) => {
          if (state.selected) {
            // Convert string IDs to numbers
            selectedStates.push(Number(state.id));
          }
          state.bezirke.forEach((district) => {
            if (district.selected) {
              // Convert string IDs to numbers
              selectedDistricts.push(Number(district.id));
              console.log(
                'Added selected district:',
                district.name,
                'ID:',
                district.id
              );
            }
            district.orte.forEach((location) => {
              if (location.selected) {
                selectedLocations.push(location);
                console.log(
                  'Added selected location:',
                  location.name,
                  'ID:',
                  location.id
                );
              }
            });
          });
        });

        console.log('Collected selections:', {
          states: selectedStates,
          districts: selectedDistricts,
          locations: selectedLocations.map((l) => l.name),
        });

        // Update available districts based on selected states
        if (selectedStates.length > 0) {
          this.updateAvailableDistricts(selectedStates);
        }

        // Clear form first to avoid any stale values
        this.locationForm.get('selectedStates')?.setValue([]);
        this.locationForm.get('selectedDistricts')?.setValue([]);
        this.locationForm.get('selectedLocations')?.setValue([]);

        // Set the form values - make sure to use explicit setValue to trigger valueChanges
        this.locationForm
          .get('allAustria')
          ?.setValue(this.offer?.allAustria || false, { emitEvent: false });

        // Use setTimeout to ensure form values are set after allAustria handling
        setTimeout(() => {
          if (!this.offer?.allAustria) {
            this.locationForm
              .get('selectedStates')
              ?.setValue(selectedStates, { emitEvent: true });
            this.locationForm
              .get('selectedDistricts')
              ?.setValue(selectedDistricts, { emitEvent: true });
            this.locationForm
              .get('selectedLocations')
              ?.setValue(selectedLocations, { emitEvent: true });
          }

          // Update the sets used for filtering
          this.selectedStatesSet = new Set(selectedStates);
          this.selectedDistrictsSet = new Set(selectedDistricts);

          // Force form validation and update
          this.locationForm.updateValueAndValidity();
        }, 0);
      }
    }

    // Handle allAustria checkbox changes
    this.locationForm.get('allAustria')?.valueChanges.subscribe((checked) => {
      if (checked) {
        this.locationForm.get('selectedStates')?.disable();
        this.locationForm.get('selectedDistricts')?.disable();
        this.locationForm.get('locationSearch')?.disable();
        this.locationForm.get('selectedLocations')?.setValue([]);
        this.locationForm.get('selectedStates')?.setValue([]);
        this.locationForm.get('selectedDistricts')?.setValue([]);
      } else {
        this.locationForm.get('selectedStates')?.enable();
        this.locationForm.get('selectedDistricts')?.enable();
        this.locationForm.get('locationSearch')?.enable();
      }
      this.onChange(this.getFormValue());
    });

    // Update sets when states change
    this.locationForm
      .get('selectedStates')
      ?.valueChanges.subscribe((states) => {
        this.selectedStatesSet = new Set(states);
        this.locationCache.clear(); // Clear cache when states change
        this.updateAvailableDistricts(states);
        this.locationForm.get('selectedDistricts')?.setValue([]);
        this.locationForm.get('selectedLocations')?.setValue([]);
        this.onChange(this.getFormValue());
        this.locationSearchSubject.next('');
      });

    // Update sets when districts change
    this.locationForm
      .get('selectedDistricts')
      ?.valueChanges.subscribe((districts) => {
        this.selectedDistrictsSet = new Set(districts);
        this.locationCache.clear(); // Clear cache when districts change
        this.locationForm.get('selectedLocations')?.setValue([]);
        this.onChange(this.getFormValue());
        this.locationSearchSubject.next('');
      });

    // Subscribe to search input changes
    this.locationForm.get('locationSearch')?.valueChanges.subscribe((value) => {
      this.locationSearchSubject.next(value || '');
    });

    // Handle location selection changes
    this.locationForm.get('selectedLocations')?.valueChanges.subscribe(() => {
      this.onChange(this.getFormValue());
    });
  }

  private updateAvailableDistricts(selectedStateIds: number[]): void {
    const districts = new Set<District>();
    selectedStateIds.forEach((stateId) => {
      const state = this.states.find((s) => s.id === stateId);
      if (state) {
        state.bezirke.forEach((district) => districts.add(district));
      }
    });
    this.districts = Array.from(districts).sort((a, b) =>
      a.name.localeCompare(b.name, 'de')
    );
  }

  // ControlValueAccessor implementation
  writeValue(value: LocationSelectorValue): void {
    if (value) {
      this.locationForm.patchValue(value, { emitEvent: false });
      if (value.selectedStates?.length) {
        this.updateAvailableDistricts(value.selectedStates);
      }
    }
  }

  registerOnChange(fn: (value: LocationSelectorValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.locationForm.disable();
    } else {
      this.locationForm.enable();
    }
  }

  private flattenLocations(hierarchy: LocationHierarchy): Location[] {
    const locations: Location[] = [];
    for (const state of hierarchy) {
      for (const district of state.bezirke) {
        locations.push(...district.orte);
      }
    }
    return locations;
  }

  private _filterLocations(value: string): Location[] {
    const filterValue = value.toLowerCase();
    const cacheKey = `${filterValue}-${Array.from(this.selectedStatesSet).join(
      ','
    )}-${Array.from(this.selectedDistrictsSet).join(',')}`;

    // Check cache first
    if (this.locationCache.has(cacheKey)) {
      return this.locationCache.get(cacheKey)!;
    }

    // If no states selected, return empty array
    if (this.selectedStatesSet.size === 0) {
      return [];
    }

    // Filter locations based on selected states and districts
    const filteredLocations: Location[] = [];

    for (const stateId of this.selectedStatesSet) {
      const state = this.states.find((s) => s.id === stateId);
      if (!state) continue;

      for (const district of state.bezirke) {
        // Skip if districts are selected and this district isn't selected
        if (
          this.selectedDistrictsSet.size > 0 &&
          !this.selectedDistrictsSet.has(district.id)
        ) {
          continue;
        }

        for (const location of district.orte) {
          if (
            location.name.toLowerCase().includes(filterValue) ||
            location.plz.includes(filterValue)
          ) {
            filteredLocations.push(location);
          }
        }
      }
    }

    // Cache the results
    this.locationCache.set(cacheKey, filteredLocations);
    return filteredLocations;
  }

  isLocationSelected(location: Location): boolean {
    const currentLocations =
      this.locationForm.get('selectedLocations')?.value || [];
    return currentLocations.some((l: Location) => l.id === location.id);
  }

  onLocationSelected(location: Location): void {
    const currentLocations = [
      ...(this.locationForm.get('selectedLocations')?.value || []),
    ];
    const isSelected = currentLocations.some(
      (l: Location) => l.id === location.id
    );

    let newLocations: Location[];
    if (isSelected) {
      newLocations = currentLocations.filter(
        (l: Location) => l.id !== location.id
      );
    } else {
      newLocations = [...currentLocations, location];
    }

    // Set the value and explicitly trigger onChange with the new value
    this.locationForm.get('selectedLocations')?.setValue(newLocations);
    this.onChange({
      ...this.getFormValue(),
      selectedLocations: newLocations,
    });
  }

  removeLocation(location: Location): void {
    const currentLocations = [
      ...(this.locationForm.get('selectedLocations')?.value || []),
    ];
    const newLocations = currentLocations.filter(
      (l: Location) => l.id !== location.id
    );

    this.locationForm.get('selectedLocations')?.setValue(newLocations);
    this.onChange({
      ...this.getFormValue(),
      selectedLocations: newLocations,
    });
  }

  removeState(stateId: number): void {
    const currentStates = this.locationForm.get('selectedStates')?.value || [];
    this.locationForm
      .get('selectedStates')
      ?.setValue(currentStates.filter((s: number) => s !== stateId));
  }

  removeDistrict(districtId: number): void {
    const currentDistricts =
      this.locationForm.get('selectedDistricts')?.value || [];
    this.locationForm
      .get('selectedDistricts')
      ?.setValue(currentDistricts.filter((d: number) => d !== districtId));
  }

  displayFn(location: Location): string {
    return location ? `${location.name} (${location.plz})` : '';
  }

  getStateName(stateId: number): string {
    return this.states.find((s) => s.id === stateId)?.name || '';
  }

  getDistrictName(districtId: number): string {
    return this.districts.find((d) => d.id === districtId)?.name || '';
  }

  getFormValue(): LocationSelectorValue {
    return this.locationForm.value;
  }
}
